import { AttachMoney, Calculate, Warning } from '@mui/icons-material';
import { Box, Grid, Stack } from '@mui/material';
import type { ConciliationTypeSummary } from 'lib/api/fundsDashboard/types';
import TableCard from 'lib/designSystem/Card/TableCard';
import LineChart from 'lib/designSystem/Charts/LineChart';
import StatCard from 'lib/designSystem/Charts/StatCard';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import SubtitleWithTooltip from 'lib/designSystem/SubtitleWithTooltip';
import CustomToggleButtonGroup from 'lib/designSystem/ToggleButtonGroup';
import { BASE_COLORS } from 'lib/functions/const/colors';
import { CHART_COLORS } from 'lib/functions/const/colors';
import { formatMoney } from 'lib/functions/utils';
import React, { useMemo, useState } from 'react';

import { useFundSummaryData } from '../../context/FundSummaryDataContext';

/** Orden y color por tipo de conciliación para las StatCards */
const CONCILIATION_TYPE_ORDER: ConciliationTypeSummary['type'][] = [
  'fundPayment',
  'paidPendingAccountDocument',
  'resellNomineeApplication',
  'buyback',
];

const CONCILIATION_TYPE_COLORS: Record<
  ConciliationTypeSummary['type'],
  string
> = {
  fundPayment: BASE_COLORS.blue,
  paidPendingAccountDocument: BASE_COLORS.green,
  resellNomineeApplication: BASE_COLORS.purple,
  buyback: BASE_COLORS.orange,
};

const TOGGLE_VIEW_MODE_OPTIONS = [
  { value: 'count', label: 'Cantidad de Pagos', ariaLabel: 'count' },
  { value: 'amount', label: 'Monto de Pagos', ariaLabel: 'amount' },
];
type ViewMode = 'count' | 'amount';
const PaymentTrendsOverview: React.FC = () => {
  const [viewMode, setViewMode] = useState<'count' | 'amount'>('count');
  const {
    paymentsConsolidated: data,
    isPendingPaymentsConsolidated: isPending,
    isErrorPaymentsConsolidated: isError,
    nomineesSummary: nomineesData,
    isPendingNomineesSummary: isPendingNominees,
  } = useFundSummaryData();

  const lineChartData = useMemo(() => {
    const trends = data?.paymentTrends;
    if (!trends?.length) return [];
    const seriesKey = viewMode === 'count' ? 'payments' : 'amount';
    return trends.map((item) => ({
      name: item.date,
      [seriesKey]: seriesKey === 'payments' ? item.payments : item.amount,
    }));
  }, [data?.paymentTrends, viewMode]);

  const lineChartColors = useMemo(() => [CHART_COLORS.blue], []);

  const conciliationTypeCards = useMemo(() => {
    const byType = data?.byConciliationType ?? [];
    const byTypeMap = new Map(byType.map((t) => [t.type, t]));
    return CONCILIATION_TYPE_ORDER.map((type) => {
      const item = byTypeMap.get(type);
      return {
        type,
        label: item?.typeEsp ?? type,
        amount: item?.amount ?? 0,
        count: item?.count ?? 0,
        color: CONCILIATION_TYPE_COLORS[type],
      };
    });
  }, [data?.byConciliationType]);

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={4}>
          <TableCard>
            {isError && (
              <Box sx={{ py: 2, color: 'error.main' }}>
                Error al cargar datos de pagos.
              </Box>
            )}
            {!isError && (
              <Stack direction="column" spacing={2}>
                <StatCard
                  label="Monto pendiente de pago"
                  value={formatMoney(
                    data?.pendingFromArrear.totalPendingAmount ?? 0,
                  )}
                  valueLabel={`CLP (${data?.pendingFromArrear.resellsWithPendingBalance ?? 0} documentos)`}
                  color={BASE_COLORS.red}
                  icon={<Warning sx={{ fontSize: 24 }} />}
                  loading={isPending}
                />
                {conciliationTypeCards.map((card) => (
                  <StatCard
                    key={card.type}
                    label={card.label}
                    value={formatMoney(card.amount)}
                    valueLabel={`CLP (${card.count} pagos)`}
                    color={card.color}
                    icon={<AttachMoney sx={{ fontSize: 24 }} />}
                    loading={isPending}
                  />
                ))}

                <StatCard
                  label="Monto promedio del total por nómina"
                  value={formatMoney(
                    nomineesData?.averageAmountPerNominee ?? 0,
                  )}
                  valueLabel="CLP"
                  color={BASE_COLORS.brown}
                  icon={<Calculate sx={{ fontSize: 24 }} />}
                  loading={isPendingNominees}
                />
              </Stack>
            )}
          </TableCard>
        </Grid>
        <Grid item xs={8}>
          <TableCard sx={{ minHeight: 400 }}>
            <SubtitleWithTooltip
              title="Tendencias de Pagos"
              tooltipContent="Evolución del monto total de pagos por período."
              tooltipPlacement="right"
              variant="compact"
            >
              <CustomToggleButtonGroup
                value={viewMode}
                onChange={(value) => setViewMode(value as ViewMode)}
                options={TOGGLE_VIEW_MODE_OPTIONS}
              />
            </SubtitleWithTooltip>
            {isPending && (
              <Box
                sx={{
                  minHeight: 300,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CenteredCircularProgress relative />
              </Box>
            )}
            {!isPending && isError && (
              <Box sx={{ py: 2, color: 'error.main' }}>
                Error al cargar tendencias de pagos
              </Box>
            )}
            {!isPending &&
              !isError &&
              data !== undefined &&
              (lineChartData.length === 0 ? (
                <Box sx={{ py: 2, color: 'text.secondary' }}>
                  No hay datos disponibles para mostrar las tendencias de pagos
                </Box>
              ) : (
                <Box sx={{ mt: 5 }}>
                  <LineChart
                    key={`paymentTrends-${viewMode}`}
                    data={lineChartData}
                    colors={lineChartColors}
                    showLegend={false}
                    height={290}
                    showArea={false}
                    yAxisFormat={viewMode === 'amount' ? 'M' : 'auto'}
                    dashLastSeries={true}
                  />
                  <Grid container spacing={2} sx={{ mt: 3, p: 2 }}>
                    <Grid item xs={6}>
                      <StatCard
                        label="Tiempo promedio de aviso de pago a fondo"
                        value={data?.averagePaymentTime ?? 0}
                        valueLabel="días"
                        color={BASE_COLORS.yellow}
                        icon={<Calculate sx={{ fontSize: 24 }} />}
                        loading={isPending}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <StatCard
                        label="Monto promedio por recaudación"
                        value={formatMoney(data?.averagePaymentAmount ?? 0)}
                        valueLabel="CLP"
                        color={BASE_COLORS.green}
                        icon={<Calculate sx={{ fontSize: 24 }} />}
                        loading={isPending}
                      />
                    </Grid>
                  </Grid>
                </Box>
              ))}
          </TableCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PaymentTrendsOverview;
